import type {CSSProperties} from "react";
import * as React from "react"
import {Toast as ToastPrimitive} from "@base-ui/react/toast"

import {cn} from "@/lib/utils"
import {Button} from "@/components/ui/button"
import {CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon, XIcon} from "lucide-react"
import {toast} from "@/hooks/toast"
import {toastVariants} from "@/components/ui/variants/toastVariants.ts";


function ToastProvider({...props}: ToastPrimitive.Provider.Props) {
    return <ToastPrimitive.Provider {...props} />
}

function ToastPortal({...props}: ToastPrimitive.Portal.Props) {
    return <ToastPrimitive.Portal data-slot="toast-portal" {...props} />
}

function ToastViewport({className, ...props}: ToastPrimitive.Viewport.Props) {
    return (
        <ToastPrimitive.Viewport data-slot="toast-viewport" className={cn(
            "pointer-events-none fixed inset-x-4 bottom-4 z-50 mx-auto w-auto max-w-sm outline-none sm:right-4 sm:left-auto sm:mx-0 sm:w-full",
            className
        )}
                                 {...props}
        />
    )
}

function Toast({className, toast, ...props}: ToastPrimitive.Root.Props) {
    return (
        <ToastPrimitive.Root data-slot="toast" className={cn(
            toastVariants({type: (toast.type as 'success' | 'warn' | 'error' | undefined) ?? "default"}),
            className
        )} toast={toast}
                             {...props}
        />
    )
}

function ToastContent({className, ...props}: ToastPrimitive.Content.Props) {
    return (
        <ToastPrimitive.Content data-slot="toast-content" className={cn(
            "flex h-full items-center gap-3 overflow-hidden p-4 transition-opacity duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] data-behind:opacity-0 data-expanded:opacity-100",
            className
        )}
                                {...props}
        />
    )
}

function ToastTitle({className, ...props}: ToastPrimitive.Title.Props) {
    return (
        <ToastPrimitive.Title data-slot="toast-title" className={cn("text-base font-medium", className)}
                              {...props}
        />
    )
}

function ToastDescription({
                              className,
                              ...props
                          }: ToastPrimitive.Description.Props) {
    return (
        <ToastPrimitive.Description data-slot="toast-description"
                                    className={cn("text-sm text-muted-foreground group-data-[type=success]/toast:text-green-700 group-data-[type=success]/toast:opacity-70 group-data-[type=warn]/toast:text-orange-500 group-data-[type=warn]/toast:opacity-70 group-data-[type=error]/toast:text-destructive group-data-[type=error]/toast:opacity-70", className)}
                                    {...props}
        />
    )
}

function ToastAction({
                         className,
                         render = <Button variant="outline" size="sm"/>,
                         ...props
                     }: ToastPrimitive.Action.Props) {
    return (
        <ToastPrimitive.Action data-slot="toast-action" render={render} className={cn("shrink-0", className)}
                               {...props}
        />
    )
}

function ToastClose({
                        className,
                        children,
                        render = <Button variant="ghost" size="icon-sm"/>,
                        ...props
                    }: ToastPrimitive.Close.Props) {
    return (
        <ToastPrimitive.Close data-slot="toast-close" aria-label="Close toast" render={render} className={cn(
            "relative shrink-0 text-muted-foreground after:absolute after:-inset-2 after:content-[''] hover:text-foreground",
            className
        )}
                              {...props}
        >
            {children ?? (
                <XIcon aria-hidden="true"/>
            )}
        </ToastPrimitive.Close>
    )
}

function ToastIcon({type}: { type: string | undefined }) {
    let icon: React.ReactNode = null

    if (type === "success") {
        icon = (
            <CircleCheckIcon aria-hidden="true"/>
        )
    }

    if (type === "info") {
        icon = (
            <InfoIcon aria-hidden="true"/>
        )
    }

    if (type === "warning") {
        icon = (
            <TriangleAlertIcon aria-hidden="true"/>
        )
    }

    if (type === "error") {
        icon = (
            <OctagonXIcon className="text-destructive" aria-hidden="true"/>
        )
    }

    if (type === "loading") {
        icon = (
            <Loader2Icon className="animate-spin" aria-hidden="true"/>
        )
    }

    if (!icon) {
        return null
    }

    return (
        <span data-slot="toast-icon"
              className="shrink-0 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4">
      {icon}
    </span>
    )
}

const toastViewport = {
    "--toast-swipe-movement-y": "-80px"
}

function ToastList() {
    const {toasts} = ToastPrimitive.useToastManager()

    return toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} style={toastViewport as CSSProperties}>
            <ToastContent>
                <ToastIcon type={toastItem.type}/>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <ToastTitle/>
                    <ToastDescription/>
                </div>
                <ToastAction/>
                <ToastClose/>
            </ToastContent>
        </Toast>
    ))
}

function Toaster({
                     children,
                     toastManager = toast,
                     ...props
                 }: ToastPrimitive.Provider.Props) {
    return (
        <ToastProvider toastManager={toastManager} {...props}>
            {children}
            <ToastPortal>
                <ToastViewport>
                    <ToastList/>
                </ToastViewport>
            </ToastPortal>
        </ToastProvider>
    )
}


export {
    Toaster,
    Toast,
    ToastAction,
    ToastClose,
    ToastContent,
    ToastDescription,
    ToastPortal,
    ToastProvider,
    ToastTitle,
    ToastViewport,
}
